/**
 * What only LMNT adds to the Nucleus dock. The dock itself — the kit's NucleusDock, the
 * gateway calls, the `?prompt=` link, the open report on /reporting, the Plan Review
 * hand-off, dev replays — is core's standard one, mounted by @atomic/app-shell for every
 * client (2026-10-07; this app's own mount, containers/AssistantChatView, is retired).
 *
 * The shell calls this hook inside the dock mount (`nucleus.useHost`), so it reads this
 * app's contexts:
 * - `references`: the `@` list — the QueryViz reports AND the master-data pull sources
 *   (NUC-217). Reports alone are the shell's default; pull sources are LMNT's gateway's.
 * - `activeReport`: LMNT hosts reports on /demand too, with its draft-project selector.
 * - `pendingChanges`: the unsaved-work registry a navigation checks.
 *
 * No `extraUiContext`: the run the page is showing (NUC-258) is published by the run
 * selector itself (useRunSelector → usePublishActiveRun) and the shell's dock sends it as
 * `active_run`; one set here would override it.
 */
import { useApolloClient } from "@apollo/client/react";
import { AuthenticationContext, isRoot as isRootRole } from "@atomic/app-shell";
import { useCallback, useContext } from "react";
import { activeReportFromLocation } from "./nucleusActiveReport";
import { describePendingChanges, hasPendingChanges } from "./pendingChanges";
import { ListQueryvizReportsDocument, type ListQueryvizReportsQuery } from "./services/__generated__/graphql";
import { GET_MY_PULL_SOURCES } from "./hooks/useMappingGaps";
import { QUERYVIZ_ORG_SLUG, QUERYVIZ_PROJECT_SLUG } from "./containers/ReportingView/constants";
import { parseCategory } from "./containers/ReportingView/category";

/** Reports marked internal-only in their notes stay off the @ list for non-root users (as on the Reporting page). */
const INTERNAL_ONLY_RE = /#internalonly/i;

interface PullSourceRow {
  name: string;
  displayName?: string | null;
  description?: string | null;
}

const PENDING_CHANGES = { has: hasPendingChanges, describe: describePendingChanges };
// Read from the LIVE location: in-frame edits rewrite the URL without a re-render.
const ACTIVE_REPORT = () => activeReportFromLocation(window.location);

export function useNucleusHost() {
  const apollo = useApolloClient();
  const { role, roles } = useContext(AuthenticationContext);
  const root = isRootRole(role) || (roles ?? []).some((r) => isRootRole(r));

  const references = useCallback(async () => {
    const [reports, pulls] = await Promise.all([
      apollo
        .query<ListQueryvizReportsQuery>({
          query: ListQueryvizReportsDocument,
          variables: { orgSlug: QUERYVIZ_ORG_SLUG, projectSlug: QUERYVIZ_PROJECT_SLUG },
          fetchPolicy: "cache-first",
        })
        .then(({ data }) => data?.listQueryvizReports ?? [])
        .catch(() => []),
      apollo
        .query<{ getMyPullSources: PullSourceRow[] }>({ query: GET_MY_PULL_SOURCES, fetchPolicy: "cache-first" })
        .then(({ data }) => data?.getMyPullSources ?? [])
        .catch(() => []),
    ]);
    return {
      reports: reports.flatMap((r) =>
        r && typeof r.name === "string" && typeof r.slug === "string" && (root || !INTERNAL_ONLY_RE.test(r.notes ?? ""))
          ? [{ name: r.name, slug: r.slug, category: r.category ? parseCategory(r.category)?.display ?? r.category : null }]
          : [],
      ),
      datasets: pulls.flatMap((d) =>
        d && typeof d.name === "string"
          ? [{ name: d.name, displayName: d.displayName ?? null, description: d.description ?? null }]
          : [],
      ),
    };
  }, [apollo, root]);

  return { references, activeReport: ACTIVE_REPORT, pendingChanges: PENDING_CHANGES };
}
